import React, { useState, useEffect } from "react";
import {
  Upload,
  Download,
  Loader,
  AlertCircle,
  FileText,
  ArrowLeft,
  Users,
  Search,
  X,
  CheckCircle,
} from "lucide-react";
import WhatsAppPreview from "./WhatsAppNewPreview";
import { getTemplates } from "../apis/template";
import { getSegments } from "../apis/segments";

const CreateCampaignForm = ({ onCancel, onSubmit }) => {
  const [selectedWaba, setSelectedWaba] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTemplates, setSelectedTemplates] = useState([]); // Array of selected template IDs
  const [selectedTemplateForPreview, setSelectedTemplateForPreview] = useState(null); // For preview display
  const [originalTemplate, setOriginalTemplate] = useState(null); // Store original template for parameter extraction
  const [useSegment, setUseSegment] = useState(false);
  const [templateValidationError, setTemplateValidationError] = useState("");
  const [templateSearchQuery, setTemplateSearchQuery] = useState(""); // Template search query

  const [campaignData, setCampaignData] = useState({
    name: "",
    description: "",
    templateIds: [], // Array of template IDs
    whatsapp_account_id: "",
    segment_id: "",
    use_segment: false,
    csvFile: null,
    csvData: [],
    sendOption: "now",
    scheduledDate: "", // Date part: YYYY-MM-DD
    scheduledTime: "", // Time part: HH:MM
    scheduledTimestamp: "", // Combined format: YYYY-MM-DD HH:MM:ss
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });

  const [errors, setErrors] = useState({});
  const [csvValidationErrors, setCsvValidationErrors] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [defaultValues, setDefaultValues] = useState({});

  useEffect(() => {
    const savedWaba = localStorage.getItem("selectedWabaAccount");
    if (savedWaba) {
      try {
        const parsed = JSON.parse(savedWaba);
        setSelectedWaba(parsed);
      } catch (err) {
        console.error("Error parsing saved WABA account:", err);
      }
    }

    const handleWabaChange = (event) => {
      setSelectedWaba(event.detail);
      loadTemplates(event.detail?.wabaId);
    };

    window.addEventListener("wabaAccountChanged", handleWabaChange);
    return () => {
      window.removeEventListener("wabaAccountChanged", handleWabaChange);
    };
  }, []);

  useEffect(() => {
    if (selectedWaba?.wabaId) {
      loadTemplates(selectedWaba.wabaId);
      loadSegments();
      // Set WhatsApp account ID from selected WABA (use phoneId as identifier)
      if (selectedWaba.phoneId) {
        handleInputChange("whatsapp_account_id", selectedWaba.phoneId);
      }
    }
  }, [selectedWaba]);

  const loadTemplates = async (wabaId) => {
    if (!wabaId) return;
    try {
      setLoading(true);
      const token = localStorage.getItem("userAccessToken");
      // Load all templates (not filtered by WABA) for better selection
      const response = await getTemplates(token, { limit: 1000, offset: 0, wabaId: wabaId });

      // API returns metaTemplates, not templates
      const rawTemplates = response.data.metaTemplates || response.data.templates || [];
      // Filter for approved templates
      const filtered = rawTemplates.filter(
        (t) => (t.status === "approved" || t.status === "APPROVED")
      );
      setTemplates(filtered);
    } catch (err) {
      console.error("Failed to load templates:", err);
      setTemplates([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const loadSegments = async () => {
    try {
      const token = localStorage.getItem("userAccessToken");
      const response = await getSegments(token, { status: "active" });
      setSegments(response.data.segments || []);
    } catch (err) {
      console.error("Failed to load segments:", err);
    }
  };

  const handleInputChange = (field, value) => {
    setCampaignData((prev) => {
      const updated = { ...prev, [field]: value };
      
      // Auto-combine date and time into scheduledTimestamp format
      if (field === "scheduledDate" || field === "scheduledTime") {
        const date = field === "scheduledDate" ? value : prev.scheduledDate;
        const time = field === "scheduledTime" ? value : prev.scheduledTime;
        
        if (date && time) {
          // Convert HH:MM to HH:MM:00 (add seconds)
          const timeWithSeconds = time.length === 5 ? `${time}:00` : time;
          updated.scheduledTimestamp = `${date} ${timeWithSeconds}`;
        } else {
          updated.scheduledTimestamp = "";
        }
      }
      
      return updated;
    });
    
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
    
    // Clear related errors when date/time changes
    if (field === "scheduledDate" || field === "scheduledTime") {
      if (errors.scheduledTimestamp) {
        setErrors((prev) => ({ ...prev, scheduledTimestamp: "" }));
      }
    }
  };

  // Transform template from API format to preview format
  const transformTemplateForPreview = (template) => {
    if (!template) {
      console.log("No template provided to transform");
      return null;
    }
    
    // If template already has body and doesn't have template_json, return as is
    if (template.body && !template.template_json) {
      return {
        ...template,
        headerFormat: template.headerFormat || template.headerType || "NONE",
        buttons: template.buttons || [],
        variables: template.variables || [],
      };
    }
    
    // Parse template_json
    let templateJson = template.template_json || {};
    if (typeof templateJson === 'string') {
      try {
        templateJson = JSON.parse(templateJson);
      } catch (e) {
        console.error("Error parsing template_json:", e);
        templateJson = {};
      }
    }
    
    // Check if template_json has components array
    const components = templateJson.components || [];
    
    // If no components found, return template with defaults
    if (components.length === 0) {
      console.warn("No components found in template_json, using defaults");
      return {
        ...template,
        body: template.body || '',
        footer: template.footer || '',
        headerFormat: template.headerFormat || template.headerType || "NONE",
        headerText: template.headerText || '',
        header_media_url: template.header_media_url || null,
        buttons: template.buttons || [],
        variables: template.variables || [],
      };
    }
    
    // Extract body from BODY component
    const bodyComponent = components.find(c => c.type === 'BODY');
    const body = bodyComponent?.text || template.body || '';
    
    // Extract footer from FOOTER component
    const footerComponent = components.find(c => c.type === 'FOOTER');
    const footer = footerComponent?.text || template.footer || '';
    
    // Extract header from HEADER component
    const headerComponent = components.find(c => c.type === 'HEADER');
    const headerFormat = headerComponent?.format?.toUpperCase() || template.headerFormat || template.headerType?.toUpperCase() || 'NONE';
    const headerText = headerComponent?.text || template.headerText || '';
    
    // Use template.header_media_url (actual URL) for display
    const headerMediaUrl = template.header_media_url || null;
    
    // Extract buttons from BUTTONS component
    const buttonsComponent = components.find(c => c.type === 'BUTTONS');
    const rawButtons = buttonsComponent?.buttons || template.buttons || [];
    const buttons = rawButtons.map((btn, index) => ({
      id: index,
      type: btn.type,
      text: btn.text || btn.url || btn.phone_number || `Button ${index + 1}`,
      url: btn.url,
      phone_number: btn.phone_number,
    }));
    
    // Extract variables from body text
    const variableMatches = body.match(/\{\{(\d+)\}\}/g) || [];
    const variables = variableMatches.map((match, index) => {
      const varNum = match.replace(/\{\{|\}\}/g, '');
      return {
        id: index,
        name: varNum,
        sample: bodyComponent?.example?.body_text?.[index] || `Sample ${varNum}`,
      };
    });
    
    return {
      ...template,
      body,
      footer,
      headerFormat,
      headerText,
      header_media_url: headerMediaUrl,
      buttons,
      variables,
    };
  };

  /**
   * Validate that selected templates have compatible parameter requirements
   * Simple approach: Compare complete CSV header arrays
   * If all templates generate the same CSV headers, they are compatible
   * @param {Array<Object>} selectedTemplateObjects - Array of template objects
   * @returns {Object} { valid: boolean, error: string }
   */
  const validateTemplateCompatibility = (selectedTemplateObjects) => {
    if (selectedTemplateObjects.length === 0) {
      return { valid: true, error: "" };
    }

    if (selectedTemplateObjects.length === 1) {
      return { valid: true, error: "" };
    }

    // Generate CSV headers for each template
    const templateHeaders = selectedTemplateObjects.map(template => {
      const headers = generateCSVHeaders(template);
      // Sort headers to ensure consistent comparison (phone_number should be first)
      const sortedHeaders = [...headers].sort((a, b) => {
        if (a === 'phone_number') return -1;
        if (b === 'phone_number') return 1;
        return a.localeCompare(b);
      });
      return {
        templateId: template.id,
        templateName: template.name,
        headers: sortedHeaders,
        headersString: sortedHeaders.join(', '),
      };
    });

    // Get first template's headers as reference
    const firstTemplateHeaders = templateHeaders[0].headers;
    const firstTemplateHeadersString = firstTemplateHeaders.join(', ');

    // Check if all templates have the same headers
    const allMatch = templateHeaders.every(t => {
      if (t.headers.length !== firstTemplateHeaders.length) {
        return false;
      }
      // Compare sorted arrays
      return t.headers.every((header, index) => header === firstTemplateHeaders[index]);
    });

    if (!allMatch) {
      const headersStr = templateHeaders.map(t => 
        `"${t.templateName}": [${t.headersString}]`
      ).join('; ');
      
      return {
        valid: false,
        error: `Templates have different CSV requirements. Each template needs different columns: ${headersStr}. All templates must require the same CSV columns.`,
      };
    }

    return { valid: true, error: "" };
  };

  /**
   * Handle template selection (checkbox toggle)
   * @param {string|number} templateId - Template ID to toggle
   */
  const handleTemplateToggle = (templateId) => {
    const normalizedTemplateId = String(templateId).trim();
    const currentSelected = [...selectedTemplates];
    
    // Check if template is already selected
    const index = currentSelected.findIndex(id => String(id).trim() === normalizedTemplateId);
    
    let newSelected;
    if (index >= 0) {
      // Deselect template
      newSelected = currentSelected.filter(id => String(id).trim() !== normalizedTemplateId);
    } else {
      // Select template
      newSelected = [...currentSelected, normalizedTemplateId];
    }

    setSelectedTemplates(newSelected);
    handleInputChange("templateIds", newSelected);

    // Validate template compatibility
    if (newSelected.length > 0) {
      const selectedTemplateObjects = newSelected
        .map(id => templates.find(t => String(t.id).trim() === String(id).trim()))
        .filter(Boolean);

      const validation = validateTemplateCompatibility(selectedTemplateObjects);
      setTemplateValidationError(validation.error);

      if (validation.valid && selectedTemplateObjects.length > 0) {
        // Use first template for preview and parameter extraction
        const firstTemplate = selectedTemplateObjects[0];
        const transformedTemplate = transformTemplateForPreview(firstTemplate);
        // Add all selected templates info to preview data
        transformedTemplate.selectedTemplates = selectedTemplateObjects.map(t => ({
          id: t.id,
          name: t.name,
          category: t.category,
        }));
        setSelectedTemplateForPreview(transformedTemplate);
        setOriginalTemplate(firstTemplate);

        // Reset default values based on first template
        const requiredParams = extractRequiredParameters(firstTemplate);
        const newDefaults = {};
        requiredParams.forEach(param => {
          newDefaults[param.key] = '';
        });
        setDefaultValues(newDefaults);
      } else {
        setSelectedTemplateForPreview(null);
        setOriginalTemplate(null);
        setDefaultValues({});
      }
    } else {
      setSelectedTemplateForPreview(null);
      setOriginalTemplate(null);
      setDefaultValues({});
      setTemplateValidationError("");
    }
  };

  /**
   * Simple CSV parser that handles quoted values
   */
  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextChar = line[i + 1];

      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          current += '"';
          i++; // Skip next quote
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  };

  const handleCSVUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith(".csv")) {
      setErrors((prev) => ({ ...prev, csvFile: "Please upload a CSV file" }));
      setCsvValidationErrors([]);
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target.result;
        const lines = text.split("\n").filter((line) => line.trim());
        
        if (lines.length === 0) {
          setErrors((prev) => ({ ...prev, csvFile: "CSV file is empty" }));
          return;
        }

        // Parse headers
        const headers = parseCSVLine(lines[0]).map((h) => h.replace(/^"|"$/g, '').trim());
        
        // Validate phone_number column exists
        const hasPhoneColumn = headers.some(h => 
          h.toLowerCase() === 'phone_number' || 
          h.toLowerCase() === 'phone' ||
          h.toLowerCase() === 'phone number'
        );

        if (!hasPhoneColumn) {
          setErrors((prev) => ({ 
            ...prev, 
            csvFile: "CSV must contain a 'phone_number' column" 
          }));
          return;
        }

        // Parse data rows (first 5 rows for preview)
        const data = lines.slice(1, 6).map((line) => {
          const values = parseCSVLine(line).map((v) => v.replace(/^"|"$/g, '').trim());
          const row = {};
          headers.forEach((header, index) => {
            row[header] = values[index] || "";
          });
          return row;
        });

        setCampaignData((prev) => ({
          ...prev,
          csvFile: file,
          csvData: data,
        }));
        setErrors((prev) => ({ ...prev, csvFile: "" }));
        setCsvValidationErrors([]); // Clear previous validation errors
      } catch (error) {
        console.error("Error parsing CSV:", error);
        setErrors((prev) => ({ 
          ...prev, 
          csvFile: "Error parsing CSV file. Please check the format." 
        }));
      }
    };
    reader.readAsText(file);
  };

  /**
   * Extract required parameters from template with their details
   * Returns array of parameter objects with component type, name, and placeholder info
   */
  const extractRequiredParameters = (template) => {
    if (!template) {
      return [];
    }

    const parameters = [];

    // Parse template_json to get component details
    let templateJson = template.template_json || {};
    if (typeof templateJson === 'string') {
      try {
        templateJson = JSON.parse(templateJson);
      } catch (e) {
        templateJson = {};
      }
    }

    const components = templateJson.components || [];

    // HEADER component variables (only TEXT format)
    const headerComponent = components.find(c => c.type === 'HEADER');
    if (headerComponent) {
      const headerFormat = headerComponent.format?.toUpperCase() || template.headerFormat?.toUpperCase() || 'NONE';
      const headerText = headerComponent.text || template.headerText || '';
      
      if (headerFormat === 'TEXT') {
        const headerVarMatches = headerText.match(/\{\{(\d+)\}\}/g) || [];
        headerVarMatches.forEach((match) => {
          const varNum = match.replace(/\{\{|\}\}/g, '');
          parameters.push({
            key: `header_${varNum}`,
            component: 'HEADER',
            varNum,
            placeholder: match,
            label: `Header Variable ${varNum}`,
            description: `Value for ${match} in header`,
          });
        });
      }
    } else if (template.headerFormat || template.headerType) {
      const headerFormat = (template.headerFormat || template.headerType).toUpperCase();
      const headerText = template.headerText || '';
      
      if (headerFormat === 'TEXT') {
        const headerVarMatches = headerText.match(/\{\{(\d+)\}\}/g) || [];
        headerVarMatches.forEach((match) => {
          const varNum = match.replace(/\{\{|\}\}/g, '');
          parameters.push({
            key: `header_${varNum}`,
            component: 'HEADER',
            varNum,
            placeholder: match,
            label: `Header Variable ${varNum}`,
            description: `Value for ${match} in header`,
          });
        });
      }
    }

    // BODY component variables (always required)
    const bodyComponent = components.find(c => c.type === 'BODY');
    const bodyText = bodyComponent?.text || template.body || '';
    const bodyVarMatches = bodyText.match(/\{\{(\d+)\}\}/g) || [];
    bodyVarMatches.forEach((match) => {
      const varNum = match.replace(/\{\{|\}\}/g, '');
      parameters.push({
        key: `body_${varNum}`,
        component: 'BODY',
        varNum,
        placeholder: match,
        label: `Body Variable ${varNum}`,
        description: `Value for ${match} in body text`,
      });
    });

    // BUTTONS component variables (only URL buttons with placeholders)
    const buttonsComponent = components.find(c => c.type === 'BUTTONS');
    const buttons = buttonsComponent?.buttons || template.buttons || [];
    buttons.forEach((btn, index) => {
      if (btn.type === 'URL' && btn.url && btn.url.includes('{{')) {
        parameters.push({
          key: `button_text_${index}`,
          component: 'BUTTON',
          index,
          placeholder: 'Button URL variable',
          label: `Button ${index + 1} Text`,
          description: `Text for URL button ${index + 1}`,
        });
      }
    });

    return parameters;
  };

  /**
   * Generate CSV headers based on template structure
   * Returns array of header names based on TemplateSender requirements
   */
  const generateCSVHeaders = (template) => {
    if (!template) {
      return ["phone_number"];
    }

    const headers = ["phone_number"];

    // Parse template_json to get component details
    let templateJson = template.template_json || {};
    if (typeof templateJson === 'string') {
      try {
        templateJson = JSON.parse(templateJson);
      } catch (e) {
        templateJson = {};
      }
    }

    const components = templateJson.components || [];

    // HEADER component variables
    const headerComponent = components.find(c => c.type === 'HEADER');
    if (headerComponent) {
      const headerFormat = headerComponent.format?.toUpperCase() || template.headerFormat?.toUpperCase() || 'NONE';
      const headerText = headerComponent.text || template.headerText || '';
      
      if (headerFormat === 'TEXT') {
        // Extract header variables and add them as header_1, header_2, etc.
        const headerVarMatches = headerText.match(/\{\{(\d+)\}\}/g) || [];
        if (headerVarMatches.length > 0) {
          headerVarMatches.forEach((match) => {
            const varNum = match.replace(/\{\{|\}\}/g, '');
            headers.push(`header_${varNum}`);
          });
        }
      }
      // Media headers (IMAGE, VIDEO, DOCUMENT) are not requested in CSV
    } else if (template.headerFormat || template.headerType) {
      // Fallback to direct template fields
      const headerFormat = (template.headerFormat || template.headerType).toUpperCase();
      const headerText = template.headerText || '';
      
      if (headerFormat === 'TEXT') {
        // Extract header variables and add them as header_1, header_2, etc.
        const headerVarMatches = headerText.match(/\{\{(\d+)\}\}/g) || [];
        if (headerVarMatches.length > 0) {
          headerVarMatches.forEach((match) => {
            const varNum = match.replace(/\{\{|\}\}/g, '');
            headers.push(`header_${varNum}`);
          });
        }
      }
      // Media headers (IMAGE, VIDEO, DOCUMENT) are not requested in CSV
    }

    // BODY component variables (required)
    const bodyComponent = components.find(c => c.type === 'BODY');
    const bodyText = bodyComponent?.text || template.body || '';
    const bodyVarMatches = bodyText.match(/\{\{(\d+)\}\}/g) || [];
    if (bodyVarMatches.length > 0) {
      bodyVarMatches.forEach((match) => {
        const varNum = match.replace(/\{\{|\}\}/g, '');
        headers.push(`body_${varNum}`);
      });
    }

    // FOOTER component (optional - only if has variables)
    const footerComponent = components.find(c => c.type === 'FOOTER');
    const footerText = footerComponent?.text || template.footer || '';
    if (footerText) {
      // Only include footer if it has variables (placeholders)
      const hasFooterVars = footerText.match(/\{\{\d+\}\}/g) && footerText.match(/\{\{\d+\}\}/g).length > 0;
      if (hasFooterVars) {
        headers.push('footer');
      }
    }

    // BUTTONS component variables
    const buttonsComponent = components.find(c => c.type === 'BUTTONS');
    const buttons = buttonsComponent?.buttons || template.buttons || [];
    buttons.forEach((btn, index) => {
      // button_payload is optional and not required in CSV
      // button_text is required only if URL button has placeholders (variables)
      if (btn.type === 'URL') {
        // Check if URL has placeholder/variable
        if (btn.url && btn.url.includes('{{')) {
          headers.push(`button_text_${index}`); // Required when URL has variables
        }
      }
      // QUICK_REPLY and PHONE_NUMBER buttons don't need variables
    });

    return headers;
  };

  /**
   * Generate sample CSV data with headers and example rows
   */
  const downloadSampleCSV = () => {
    if (!selectedTemplateForPreview || !originalTemplate) {
      alert("Please select at least one template first to download sample CSV");
      return;
    }

    const headers = generateCSVHeaders(originalTemplate);
    
    // Generate sample data rows (2 rows)
    const sampleRows = [];
    for (let i = 0; i < 2; i++) {
      const row = headers.map((header) => {
        if (header === 'phone_number') {
          return `+1234567890${i}`;
        } else if (header.startsWith('body_')) {
          return `Sample Body Var ${header.split('_')[1]}`;
        } else if (header.startsWith('header_')) {
          return `Sample Header Var ${header.split('_')[1]}`;
        } else if (header === 'footer') {
          return 'Sample Footer';
        } else if (header.startsWith('button_text_')) {
          return 'Click Here';
        }
        return '';
      });
      sampleRows.push(row.join(','));
    }

    const csvContent = [headers.join(','), ...sampleRows].join('\n');
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `campaign_sample_${originalTemplate.name || 'template'}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleSubmit = async () => {
    const newErrors = {};
    if (!campaignData.name.trim()) {
      newErrors.name = "Campaign name is required";
    }
    if (!campaignData.templateIds || campaignData.templateIds.length === 0) {
      newErrors.templateIds = "Please select at least one template";
    }
    if (templateValidationError) {
      newErrors.templateIds = templateValidationError;
    }
    if (!campaignData.whatsapp_account_id) {
      newErrors.whatsapp_account_id = "Please select a WhatsApp account";
    }
    if (useSegment) {
      if (!campaignData.segment_id) {
        newErrors.segment_id = "Please select a contact list";
      }
    } else {
      if (!campaignData.csvFile) {
        newErrors.csvFile = "Please upload a CSV file";
      }
    }
    // Validate scheduled timestamp if schedule option is selected
    if (campaignData.sendOption === "schedule") {
      // Validate date
      if (!campaignData.scheduledDate || !campaignData.scheduledDate.trim()) {
        newErrors.scheduledDate = "Please select a scheduled date";
      }
      
      // Validate time
      if (!campaignData.scheduledTime || !campaignData.scheduledTime.trim()) {
        newErrors.scheduledTime = "Please select a scheduled time";
      }
      
      // Validate combined timestamp
      if (campaignData.scheduledDate && campaignData.scheduledTime) {
        // Validate format: YYYY-MM-DD HH:MM:ss
        const timeWithSeconds = campaignData.scheduledTime.length === 5 
          ? `${campaignData.scheduledTime}:00` 
          : campaignData.scheduledTime;
        const timestamp = `${campaignData.scheduledDate} ${timeWithSeconds}`;
        const timestampRegex = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;
        
        if (!timestampRegex.test(timestamp)) {
          newErrors.scheduledTimestamp = "Invalid date or time format";
        } else {
          // Validate that scheduled time is in the future
          const [datePart, timePart] = timestamp.split(" ");
          const scheduledDate = new Date(`${datePart}T${timePart}`);
          if (isNaN(scheduledDate.getTime())) {
            newErrors.scheduledTimestamp = "Invalid date or time values";
          } else if (scheduledDate <= new Date()) {
            newErrors.scheduledTimestamp = "Scheduled time must be in the future";
          }
        }
      } else if (!campaignData.scheduledTimestamp || !campaignData.scheduledTimestamp.trim()) {
        newErrors.scheduledTimestamp = "Please select both date and time";
      }
      
      if (!campaignData.timezone || !campaignData.timezone.trim()) {
        newErrors.timezone = "Please select a timezone";
      }
    }
    setErrors(newErrors);
    setCsvValidationErrors([]); // Clear previous validation errors
    
    if (Object.keys(newErrors).length === 0) {
      setIsSubmitting(true);
      try {
        // Transform data to match backend expectations
        // Filter out empty default values (only send non-empty defaults)
        const filteredDefaults = {};
        Object.keys(defaultValues).forEach(key => {
          if (defaultValues[key] && defaultValues[key].trim() !== '') {
            filteredDefaults[key] = defaultValues[key].trim();
          }
        });

        // Format data for backend
        const submitData = {
          campaign_name: campaignData.name, // Backend expects campaign_name
          description: campaignData.description || "",
          template_ids: campaignData.templateIds, // Array of template IDs
          whatsapp_account_id: campaignData.whatsapp_account_id,
          segment_id: useSegment ? campaignData.segment_id : null,
          use_segment: useSegment,
          csvFile: !useSegment ? campaignData.csvFile : null,
          defaultValues: Object.keys(filteredDefaults).length > 0 ? filteredDefaults : null,
          variables: {}, // Can be extended later for template variables
          sendOption: campaignData.sendOption,
          // For schedule option, send scheduledTimestamp in YYYY-MM-DD HH:MM:ss format
          scheduledTimestamp: campaignData.sendOption === "schedule" 
            ? campaignData.scheduledTimestamp.trim() 
            : null,
          timezone: campaignData.sendOption === "schedule" 
            ? campaignData.timezone 
            : null,
        };
        
        await onSubmit(submitData);
      } catch (error) {
        console.error("Error creating campaign:", error);
        
        // Check if error has CSV validation errors
        const errorResponse = error.response?.data;
        if (errorResponse?.validation?.errors) {
          const validationErrors = errorResponse.validation.errors;
          const summary = errorResponse.validation.summary;
          
          // Format summary message
          let summaryMessage = errorResponse.error || "CSV validation failed";
          if (summary && typeof summary === 'object') {
            const { totalRows, validRows, invalidRows } = summary;
            summaryMessage = `CSV validation failed: ${invalidRows} of ${totalRows} rows have errors`;
          } else if (typeof summary === 'string') {
            summaryMessage = summary;
          }
          
          // Set CSV error with summary
          setErrors((prev) => ({
            ...prev,
            csvFile: summaryMessage,
          }));
          
          // Ensure validationErrors is an array and all items are strings
          let errorsArray = [];
          if (Array.isArray(validationErrors)) {
            errorsArray = validationErrors.map(err => {
              if (typeof err === 'string') {
                return err;
              } else if (typeof err === 'object') {
                // If error is an object, try to extract a meaningful message
                return JSON.stringify(err);
              }
              return String(err);
            });
          } else if (validationErrors) {
            // If it's not an array, wrap it
            errorsArray = [typeof validationErrors === 'string' ? validationErrors : JSON.stringify(validationErrors)];
          }
          
          // Set detailed validation errors
          setCsvValidationErrors(errorsArray);
        } else {
          // Other errors (non-CSV validation)
          const errorMessage = errorResponse?.error || error.message || "Failed to create campaign";
          // You can handle other errors here if needed
          console.error("Campaign creation error:", errorMessage);
        }
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-3 md:p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              onClick={onCancel}
              className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              title="Go back"
            >
              <ArrowLeft size={18} />
            </button>
            <h1 className="text-lg font-bold text-gray-900">Create Campaign</h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onCancel}
              className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className={`px-3 py-1.5 text-xs font-medium text-white bg-[#25D366] rounded-lg hover:bg-[#20BD5A] flex items-center gap-1.5 ${
                isSubmitting ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {isSubmitting && <Loader className="w-3 h-3 animate-spin" />}
              {isSubmitting
                ? "Creating..."
                : campaignData.sendOption === "now"
                ? "Create & Send Now"
                : "Create & Schedule"}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left: Form */}
          <div className="lg:col-span-2 space-y-3 lg:max-h-[calc(100vh-120px)] lg:overflow-y-auto lg:pr-2">
            {/* Campaign Info */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h2 className="text-sm font-semibold text-gray-900 mb-3">
                Campaign Information
              </h2>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Campaign Name *
                  </label>
                  <input
                    type="text"
                    value={campaignData.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    placeholder="e.g., Welcome Campaign"
                    className={`w-full px-2.5 py-1.5 text-xs border ${
                      errors.name ? "border-red-300" : "border-gray-300"
                    } rounded-lg focus:outline-none focus:ring-1 focus:ring-[#25D366]`}
                  />
                  {errors.name && (
                    <p className="text-[10px] text-red-600 mt-0.5">{errors.name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={campaignData.description}
                    onChange={(e) =>
                      handleInputChange("description", e.target.value)
                    }
                    placeholder="Describe your campaign..."
                    rows={2}
                    className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#25D366] resize-none"
                  />
                </div>
              </div>
            </div>

            {/* Template Selection */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h2 className="text-sm font-semibold text-gray-900 mb-3">
                Select Templates * (Multiple Selection)
              </h2>
              <p className="text-[10px] text-gray-600 mb-3">
                Select multiple templates to shuffle and send. All templates must have the same parameter requirements (same number of header and body parameters).
              </p>
              {!selectedWaba ? (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-xs text-yellow-800">
                    Please select a WhatsApp Business Account from the header
                  </p>
                </div>
              ) : loading ? (
                <div className="flex items-center justify-center py-6">
                  <Loader className="w-5 h-5 animate-spin text-[#25D366]" />
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Template Search */}
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={templateSearchQuery}
                      onChange={(e) => setTemplateSearchQuery(e.target.value)}
                      placeholder="Search templates by name or category..."
                      className="w-full pl-9 pr-8 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#25D366] focus:border-transparent"
                    />
                    {templateSearchQuery && (
                      <button
                        onClick={() => setTemplateSearchQuery("")}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>

                  {/* Selected Templates Review */}
                  {selectedTemplates.length > 0 && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-semibold text-green-900">
                          Selected Templates ({selectedTemplates.length})
                        </p>
                        {selectedTemplates.length > 1 && (
                          <span className="text-[10px] text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                            Will be shuffled
                          </span>
                        )}
                      </div>
                      <div className="space-y-1.5 max-h-32 overflow-y-auto">
                        {selectedTemplates.map((templateId) => {
                          const template = templates.find(
                            t => String(t.id).trim() === String(templateId).trim()
                          );
                          if (!template) return null;
                          return (
                            <div
                              key={templateId}
                              className="flex items-center justify-between p-2 bg-white rounded border border-green-200"
                            >
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium text-gray-900 truncate">
                                  {template.name}
                                </p>
                                {template.category && (
                                  <p className="text-[10px] text-gray-500">
                                    {template.category}
                                  </p>
                                )}
                              </div>
                              <button
                                onClick={() => handleTemplateToggle(templateId)}
                                className="ml-2 p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                                title="Remove template"
                              >
                                <X size={14} />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Template List */}
                  <div className="max-h-60 overflow-y-auto border border-gray-300 rounded-lg p-2 space-y-2">
                    {(() => {
                      // Filter templates based on search query
                      const filteredTemplates = templates.filter((template) => {
                        if (!templateSearchQuery.trim()) return true;
                        const query = templateSearchQuery.toLowerCase();
                        return (
                          template.name?.toLowerCase().includes(query) ||
                          template.category?.toLowerCase().includes(query) ||
                          template.language?.toLowerCase().includes(query)
                        );
                      });

                      if (filteredTemplates.length === 0) {
                        return (
                          <p className="text-xs text-gray-500 text-center py-4">
                            {templateSearchQuery
                              ? `No templates found matching "${templateSearchQuery}"`
                              : "No approved templates found"}
                          </p>
                        );
                      }

                      return filteredTemplates.map((template) => {
                        const isSelected = selectedTemplates.some(
                          id => String(id).trim() === String(template.id).trim()
                        );
                        return (
                          <label
                            key={template.id}
                            className={`flex items-start gap-2 p-2 rounded-lg cursor-pointer border transition-colors ${
                              isSelected
                                ? "bg-green-50 border-green-300 hover:bg-green-100"
                                : "hover:bg-gray-50 border-transparent hover:border-gray-200"
                            }`}
                          >
                            <div className="mt-0.5 flex-shrink-0">
                              {isSelected ? (
                                <CheckCircle className="w-4 h-4 text-green-600" />
                              ) : (
                                <div className="w-4 h-4 border-2 border-gray-300 rounded" />
                              )}
                            </div>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleTemplateToggle(template.id)}
                              className="hidden"
                            />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-gray-900">
                                {template.name}
                              </p>
                              <div className="flex items-center gap-2 mt-0.5">
                                {template.category && (
                                  <span className="text-[10px] text-gray-500">
                                    {template.category}
                                  </span>
                                )}
                                {template.language && (
                                  <span className="text-[10px] text-gray-400">
                                    • {template.language.toUpperCase()}
                                  </span>
                                )}
                              </div>
                            </div>
                          </label>
                        );
                      });
                    })()}
                  </div>
                  
                  {(errors.templateIds || templateValidationError) && (
                    <div className="p-2 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-[10px] text-red-600">
                        {errors.templateIds || templateValidationError}
                      </p>
                    </div>
                  )}
                  
                  {selectedTemplates.length > 0 && !templateValidationError && (
                    <div className="flex items-center gap-1.5 text-[10px] text-green-600">
                      <CheckCircle size={12} />
                      <span>
                        {selectedTemplates.length} template{selectedTemplates.length > 1 ? 's' : ''} selected and validated
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Default Values Section */}
            {originalTemplate && extractRequiredParameters(originalTemplate).length > 0 && (
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <h2 className="text-sm font-semibold text-gray-900 mb-3">
                  Default Values (Optional)
                </h2>
                <p className="text-[10px] text-gray-600 mb-3">
                  Set default values for template parameters. These will be used if a CSV row doesn't have a value for that parameter.
                </p>
                <div className="space-y-3">
                  {extractRequiredParameters(originalTemplate).map((param) => (
                    <div key={param.key}>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        {param.label}
                        <span className="text-[10px] text-gray-500 ml-1">
                          ({param.placeholder})
                        </span>
                      </label>
                      <input
                        type="text"
                        value={defaultValues[param.key] || ''}
                        onChange={(e) => {
                          setDefaultValues((prev) => ({
                            ...prev,
                            [param.key]: e.target.value,
                          }));
                        }}
                        placeholder={`Default value for ${param.description}`}
                        className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#25D366]"
                      />
                      <p className="text-[10px] text-gray-500 mt-0.5">
                        {param.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recipients Selection */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h2 className="text-sm font-semibold text-gray-900 mb-3">
                Recipients *
              </h2>
              
              {/* Toggle Switch */}
              {/* <div className="mb-4 flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={useSegment}
                    onChange={(e) => {
                      setUseSegment(e.target.checked);
                      handleInputChange("use_segment", e.target.checked);
                      if (!e.target.checked) {
                        handleInputChange("segment_id", "");
                      }
                    }}
                    className="w-4 h-4 text-[#25D366] border-gray-300 rounded focus:ring-[#25D366]"
                  />
                  <span className="text-xs font-medium text-gray-700">
                    Use Contact List
                  </span>
                </label>
              </div> */}

              {useSegment ? (
                // Contact List Selection
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Select Contact List *
                    </label>
                    <select
                      value={campaignData.segment_id}
                      onChange={(e) => handleInputChange("segment_id", e.target.value)}
                      className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#25D366]"
                    >
                      <option value="">-- Select Contact List --</option>
                      {segments.map((segment) => (
                        <option key={segment.segment_id} value={segment.segment_id}>
                          {segment.segment_name} ({segment.contact_count || 0} contacts)
                        </option>
                      ))}
                    </select>
                    {errors.segment_id && (
                      <p className="text-[10px] text-red-600 mt-0.5">{errors.segment_id}</p>
                    )}
                    {segments.length === 0 && (
                      <p className="text-xs text-gray-500 mt-2">
                        No contact lists found. Create one from the Contact Lists page.
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                // CSV Upload
                <div className="space-y-3">
                  <button
                    onClick={downloadSampleCSV}
                    disabled={!selectedTemplateForPreview || !originalTemplate}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                      selectedTemplateForPreview && originalTemplate
                        ? "text-[#25D366] bg-[#25D366]/10 hover:bg-[#25D366]/20 cursor-pointer"
                        : "text-gray-400 bg-gray-100 cursor-not-allowed"
                    }`}
                    title={!selectedTemplateForPreview ? "Please select at least one template first" : "Download sample CSV with template variables"}
                  >
                    <Download size={14} />
                    Download Sample CSV
                  </button>
                  {!selectedTemplateForPreview && (
                    <p className="text-[10px] text-gray-500">
                      Select at least one template to download CSV with required variables
                    </p>
                  )}

                <div>
                  <label className="block">
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-[#25D366] transition-colors cursor-pointer">
                      <Upload className="mx-auto mb-1.5 text-gray-400" size={24} />
                      <p className="text-xs font-semibold text-gray-700 mb-0.5">
                        {campaignData.csvFile
                          ? campaignData.csvFile.name
                          : "Click to upload CSV"}
                      </p>
                      <p className="text-[10px] text-gray-500">
                        CSV file with phone numbers and variables
                      </p>
                    </div>
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleCSVUpload}
                      className="hidden"
                    />
                  </label>
                  {errors.csvFile && (
                    <div className="mt-2">
                      <div className="flex items-start gap-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                        <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-[10px] font-medium text-red-800 mb-1">
                            {errors.csvFile}
                          </p>
                          {Array.isArray(csvValidationErrors) && csvValidationErrors.length > 0 && (
                            <details className="mt-1">
                              <summary className="text-[10px] text-red-600 cursor-pointer hover:text-red-800">
                                View detailed errors ({csvValidationErrors.length})
                              </summary>
                              <ul className="mt-1 ml-4 list-disc space-y-0.5 max-h-40 overflow-y-auto">
                                {csvValidationErrors.slice(0, 20).map((error, idx) => {
                                  // Ensure error is always a string
                                  const errorText = typeof error === 'string' 
                                    ? error 
                                    : typeof error === 'object' 
                                    ? JSON.stringify(error, null, 2)
                                    : String(error);
                                  
                                  return (
                                    <li key={idx} className="text-[10px] text-red-700">
                                      {errorText}
                                    </li>
                                  );
                                })}
                                {csvValidationErrors.length > 20 && (
                                  <li className="text-[10px] text-red-600 italic">
                                    ... and {csvValidationErrors.length - 20} more errors
                                  </li>
                                )}
                              </ul>
                            </details>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                  {/* CSV Preview */}
                  {campaignData.csvData.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs font-medium text-gray-700 mb-1.5">
                        CSV Preview (First 5 rows):
                      </p>
                      <div className="overflow-x-auto border border-gray-200 rounded-lg">
                        <table className="min-w-full text-[10px]">
                          <thead className="bg-gray-50">
                            <tr>
                              {Object.keys(campaignData.csvData[0] || {}).map(
                                (key) => (
                                  <th
                                    key={key}
                                    className="px-2 py-1.5 text-left font-medium text-gray-700"
                                  >
                                    {key}
                                  </th>
                                )
                              )}
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {campaignData.csvData.map((row, index) => (
                              <tr key={index}>
                                {Object.values(row).map((value, i) => (
                                  <td key={i} className="px-2 py-1.5 text-gray-900">
                                    {value}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Send Options */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h2 className="text-sm font-semibold text-gray-900 mb-3">
                Send Options
              </h2>
              <div className="space-y-3">
                {/* Radio buttons for Send Now or Schedule */}
                <div className="flex gap-4">
                  <label className="flex items-center cursor-pointer group">
                    <input
                      type="radio"
                      name="sendOption"
                      value="now"
                      checked={campaignData.sendOption === "now"}
                      onChange={(e) => {
                        handleInputChange("sendOption", e.target.value);
                        // Clear scheduled fields when switching to "now"
                        if (e.target.value === "now") {
                          handleInputChange("scheduledDate", "");
                          handleInputChange("scheduledTime", "");
                          handleInputChange("scheduledTimestamp", "");
                        }
                      }}
                      className="mr-2 w-4 h-4 text-[#25D366] border-gray-300 focus:ring-[#25D366]"
                    />
                    <span className="text-xs font-medium text-gray-700 group-hover:text-gray-900">
                      Send Now
                    </span>
                  </label>
                  <label className="flex items-center cursor-pointer group">
                    <input
                      type="radio"
                      name="sendOption"
                      value="schedule"
                      checked={campaignData.sendOption === "schedule"}
                      onChange={(e) => handleInputChange("sendOption", e.target.value)}
                      className="mr-2 w-4 h-4 text-[#25D366] border-gray-300 focus:ring-[#25D366]"
                    />
                    <span className="text-xs font-medium text-gray-700 group-hover:text-gray-900">
                      Schedule
                    </span>
                  </label>
                </div>

                {/* Schedule Options - Show when schedule is selected */}
                {campaignData.sendOption === "schedule" && (
                  <div className="pt-3 border-t border-gray-200 space-y-3">
                    {/* Date and Time Selectors */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Scheduled Date *
                        </label>
                        <input
                          type="date"
                          value={campaignData.scheduledDate}
                          onChange={(e) => handleInputChange("scheduledDate", e.target.value)}
                          min={new Date().toISOString().split("T")[0]}
                          className={`w-full px-2.5 py-1.5 text-xs border ${
                            errors.scheduledTimestamp || errors.scheduledDate
                              ? "border-red-300"
                              : "border-gray-300"
                          } rounded-lg focus:outline-none focus:ring-1 focus:ring-[#25D366]`}
                        />
                        {(errors.scheduledTimestamp || errors.scheduledDate) && (
                          <p className="text-[10px] text-red-600 mt-0.5">
                            {errors.scheduledDate || errors.scheduledTimestamp}
                          </p>
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Scheduled Time *
                        </label>
                        <input
                          type="time"
                          value={campaignData.scheduledTime}
                          onChange={(e) => handleInputChange("scheduledTime", e.target.value)}
                          step="1"
                          className={`w-full px-2.5 py-1.5 text-xs border ${
                            errors.scheduledTimestamp || errors.scheduledTime
                              ? "border-red-300"
                              : "border-gray-300"
                          } rounded-lg focus:outline-none focus:ring-1 focus:ring-[#25D366]`}
                        />
                        {(errors.scheduledTimestamp || errors.scheduledTime) && (
                          <p className="text-[10px] text-red-600 mt-0.5">
                            {errors.scheduledTime || errors.scheduledTimestamp}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {/* Display formatted timestamp */}
                    {campaignData.scheduledTimestamp && (
                      <div className="p-2 bg-gray-50 border border-gray-200 rounded-lg">
                        <p className="text-[10px] text-gray-600 mb-0.5">Formatted timestamp:</p>
                        <p className="text-xs font-mono font-medium text-gray-900">
                          {campaignData.scheduledTimestamp}
                        </p>
                      </div>
                    )}
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Timezone *
                      </label>
                      <select
                        value={campaignData.timezone}
                        onChange={(e) => handleInputChange("timezone", e.target.value)}
                        className={`w-full px-2.5 py-1.5 text-xs border ${
                          errors.timezone
                            ? "border-red-300"
                            : "border-gray-300"
                        } rounded-lg focus:outline-none focus:ring-1 focus:ring-[#25D366]`}
                      >
                        <option value="">-- Select Timezone --</option>
                        <option value="America/New_York">America/New_York (EST/EDT)</option>
                        <option value="America/Chicago">America/Chicago (CST/CDT)</option>
                        <option value="America/Denver">America/Denver (MST/MDT)</option>
                        <option value="America/Los_Angeles">America/Los_Angeles (PST/PDT)</option>
                        <option value="America/Toronto">America/Toronto</option>
                        <option value="Europe/London">Europe/London (GMT/BST)</option>
                        <option value="Europe/Paris">Europe/Paris (CET/CEST)</option>
                        <option value="Europe/Berlin">Europe/Berlin (CET/CEST)</option>
                        <option value="Asia/Dubai">Asia/Dubai (GST)</option>
                        <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
                        <option value="Asia/Singapore">Asia/Singapore (SGT)</option>
                        <option value="Asia/Tokyo">Asia/Tokyo (JST)</option>
                        <option value="Asia/Shanghai">Asia/Shanghai (CST)</option>
                        <option value="Australia/Sydney">Australia/Sydney (AEST/AEDT)</option>
                      </select>
                      {errors.timezone && (
                        <p className="text-[10px] text-red-600 mt-0.5">
                          {errors.timezone}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right: Preview */}
          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-4 lg:max-h-[calc(100vh-120px)] lg:overflow-y-auto space-y-3">
              {selectedTemplates.length > 0 ? (
                <>
                  {/* Selected Templates Summary */}
                  {selectedTemplates.length > 1 && (
                    <div className="bg-white rounded-lg border border-gray-200 p-3">
                      <h3 className="text-xs font-semibold text-gray-900 mb-2">
                        Selected Templates ({selectedTemplates.length})
                      </h3>
                      <div className="space-y-1.5 max-h-32 overflow-y-auto">
                        {selectedTemplates.map((templateId) => {
                          const template = templates.find(
                            t => String(t.id).trim() === String(templateId).trim()
                          );
                          if (!template) return null;
                          return (
                            <div
                              key={templateId}
                              className="flex items-center gap-2 p-1.5 rounded text-xs bg-gray-50 border border-gray-200"
                            >
                              <span className="flex-1 text-gray-900 truncate">{template.name}</span>
                            </div>
                          );
                        })}
                      </div>
                      <p className="text-[10px] text-gray-600 mt-2">
                        Templates will be shuffled randomly when sending
                      </p>
                    </div>
                  )}

                  {/* Variables Display (from first template) */}
                  {selectedTemplateForPreview?.variables && selectedTemplateForPreview.variables.length > 0 && (
                    <div className="bg-white rounded-lg border border-gray-200 p-3">
                      <h3 className="text-xs font-semibold text-gray-900 mb-2">
                        Template Variables
                      </h3>
                      <div className="space-y-1.5">
                        {selectedTemplateForPreview.variables.map((variable, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-200"
                          >
                            <div className="flex-1">
                              <p className="text-[10px] font-medium text-gray-700">
                                {`{{${variable.name}}}`}
                              </p>
                              <p className="text-[10px] text-gray-500 mt-0.5">
                                Sample: {variable.sample || `Variable ${variable.name}`}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                      <p className="text-[10px] text-gray-600 mt-2">
                        These variables will be replaced with CSV column values
                      </p>
                    </div>
                  )}

                  {/* All Template Previews */}
                  <div className="space-y-4">
                    {selectedTemplates.map((templateId, index) => {
                      const template = templates.find(
                        t => String(t.id).trim() === String(templateId).trim()
                      );
                      if (!template) return null;
                      
                      const transformedTemplate = transformTemplateForPreview(template);
                      if (!transformedTemplate) return null;

                      return (
                        <div key={templateId} className="space-y-2">
                          {/* Template Name Header */}
                          <div className="bg-white rounded-lg border border-gray-200 p-2">
                            <div className="flex items-center justify-between">
                              <h4 className="text-xs font-semibold text-gray-900">
                                Preview {index + 1} of {selectedTemplates.length}
                              </h4>
                              {index === 0 && selectedTemplates.length > 1 && (
                                <span className="text-[10px] text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
                                  Primary
                                </span>
                              )}
                            </div>
                            <p className="text-[10px] text-gray-600 mt-0.5 truncate">
                              {template.name}
                            </p>
                            {template.category && (
                              <p className="text-[10px] text-gray-500 mt-0.5">
                                {template.category}
                              </p>
                            )}
                          </div>

                          {/* WhatsApp Preview */}
                          <WhatsAppPreview
                            templateData={transformedTemplate}
                            defaultValues={defaultValues}
                          />
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
                  <FileText className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-xs text-gray-500">
                    Select at least one template to see preview
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateCampaignForm;

